import { useEffect, useState } from "react";
import { CheckCircle2, Clock, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { usePortal } from "@/pages/admin/PortalLayout";
import { COMPLIANCE, INFRA } from "@/lib/architecture";

const STATUS_ICON = { Implemented: CheckCircle2, "Platform managed": CheckCircle2, Mapped: CheckCircle2, Documented: CheckCircle2, Placeholder: MinusCircle };

export default function PortalGovernance() {
  const [settings] = usePortal("/portal/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (settings) setF(settings); }, [settings]);
  const save = async () => {
    try { const { data } = await api.put("/portal/settings", { ...f, otp_ttl_seconds: Number(f.otp_ttl_seconds), retention_days: Number(f.retention_days) }); setF(data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="portal-governance">
      <div><h1 className="text-2xl font-bold md:text-3xl">Security, compliance & governance</h1><p className="text-sm text-muted-foreground">Sandbox control mapping – not a certification.</p></div>
      {f && (
        <section className="grid gap-4 rounded-2xl border bg-card p-5 shadow-sm md:grid-cols-3">
          <label className="text-xs font-semibold">OTP lifetime (seconds, 60–900)
            <input data-testid="settings-otp-ttl-input" type="number" value={f.otp_ttl_seconds} onChange={(e) => setF({ ...f, otp_ttl_seconds: e.target.value })} className="mt-1 h-11 w-full rounded-xl border bg-card px-3 text-sm" />
          </label>
          <label className="text-xs font-semibold">Data retention (days)
            <input data-testid="settings-retention-input" type="number" value={f.retention_days} onChange={(e) => setF({ ...f, retention_days: e.target.value })} className="mt-1 h-11 w-full rounded-xl border bg-card px-3 text-sm" />
          </label>
          <div className="flex items-center justify-between gap-3 text-xs font-semibold">
            <span>Show OTP on screen (sandbox only)</span>
            <Switch data-testid="settings-sandbox-otp-switch" checked={!!f.sandbox_show_otp} onCheckedChange={(v) => setF({ ...f, sandbox_show_otp: v })} />
          </div>
          <button onClick={save} data-testid="settings-save-btn" className="h-11 rounded-xl bg-primary text-sm font-semibold text-primary-foreground md:col-span-3">Save settings</button>
        </section>
      )}
      <section className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
        <table className="w-full text-sm" data-testid="compliance-table">
          <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-3">Area</th><th className="p-3">Control</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {COMPLIANCE.map((c) => {
              const Icon = STATUS_ICON[c.status] || Clock;
              return (
                <tr key={c.area} className="border-t"><td className="p-3 font-semibold">{c.area}</td><td className="p-3 text-xs text-muted-foreground">{c.control}</td>
                  <td className="p-3 text-xs font-semibold"><span className="inline-flex items-center gap-1"><Icon size={14} /> {c.status}</span></td></tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className="rounded-2xl border bg-card p-5 shadow-sm">
        <h2 className="mb-2 text-base font-bold md:text-lg">Infrastructure & business continuity (informational)</h2>
        <ul className="grid gap-2 text-sm sm:grid-cols-2">{INFRA.map((x) => <li key={x} className="rounded-xl bg-muted px-3 py-2">{x}</li>)}</ul>
        <p className="mt-3 text-xs text-muted-foreground">Backups: daily snapshots with 30-day retention · RPO 1h / RTO 4h targets · multi-zone HA · documented DR runbook. Sandbox only.</p>
      </section>
    </div>
  );
}
